import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from "firebase/analytics";
import { 
    getAuth, 
    signInAnonymously, 
    onAuthStateChanged,
    signInWithCustomToken
} from 'firebase/auth';
import { 
    getFirestore, 
    collection, 
    doc, 
    addDoc, 
    setDoc, 
    onSnapshot, 
    query,
    deleteDoc,
    serverTimestamp
} from 'firebase/firestore';
import { setLogLevel } from 'firebase/app';

// --- Helper Functions & Constants ---

const firebaseConfig = {
  apiKey: "AIzaSyCtweRW5agkE4jwnQqvh_QK5TyvN1KyINM",
  authDomain: "harmony-d42dc.firebaseapp.com",
  projectId: "harmony-d42dc",
  storageBucket: "harmony-d42dc.firebasestorage.app",
  messagingSenderId: "200906352195",
  appId: "1:200906352195:web:58e5fbe059e2e1175f96f6",
  measurementId: "G-260TL9C5JZ"
};

const appId = firebaseConfig.appId;

const HABIT_COLORS = [
    { bg: 'bg-green-500', empty: 'bg-green-500/20', name: 'Green' },
    { bg: 'bg-blue-500', empty: 'bg-blue-500/20', name: 'Blue' },
    { bg: 'bg-red-500', empty: 'bg-red-500/20', name: 'Red' },
    { bg: 'bg-yellow-400', empty: 'bg-yellow-400/20', name: 'Yellow' },
    { bg: 'bg-purple-500', empty: 'bg-purple-500/20', name: 'Purple' },
];

const WELLNESS_COLORS = [
    'bg-gray-700/40', // 0% complete
    'bg-gray-500/50',
    'bg-gray-400/70',
    'bg-gray-300/80',
    'bg-gray-200/90',
    'bg-white',       // 100% complete
];


const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// --- Icon Components ---

const AddIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2">
        <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
);

const TrashIcon = ({ onClick }) => (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} className="p-1 rounded-full hover:bg-gray-700 z-10" aria-label="Delete habit">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 hover:text-red-400 transition-colors">
            <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
    </button>
);

const ChevronIcon = ({ isOpen }) => (
     <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

// --- UI Components ---
const YearNavigator = ({ selectedYear, years, onYearChange }) => (
    <div className="flex justify-center items-center flex-wrap gap-2 mb-8">
        {years.map(year => (
            <button
                key={year}
                onClick={() => onYearChange(year)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    selectedYear === year
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-700/80 text-gray-300 hover:bg-gray-700 backdrop-blur-sm border border-gray-600'
                }`}
            >
                {year}
            </button>
        ))}
    </div>
);


// --- Calendar Grid Components ---

const CalendarGrid = ({ gridData, colorLogic, onDayClick = () => {} }) => {
    const { days, monthColumns } = gridData;

    if (days.length === 0) {
        return <div className="text-center text-gray-500 p-4">No data to display for this year.</div>;
    }

    const columnWidth = 22;

    return (
        <div className="overflow-x-auto p-2 bg-gray-900/50 rounded-lg">
            <div className="inline-block align-top">
                <div className="flex text-xs text-gray-500 mb-1">
                    {monthColumns.map(({ month, startColumn }, index) => {
                        const totalColumns = Math.ceil(days.length / 7);
                        let colSpan;
                        if (index + 1 < monthColumns.length) {
                             colSpan = monthColumns[index + 1].startColumn - startColumn;
                        } else {
                             colSpan = totalColumns - startColumn + 1;
                        }
                        const width = colSpan * columnWidth;
                        return (
                            <div key={`${month}-${index}`} style={{ minWidth: `${width}px` }} className="pr-2 whitespace-nowrap">{month}</div>
                        );
                    })}
                </div>
                
                <div className="grid grid-flow-col grid-rows-7 gap-1.5">
                    {days.map((day, index) => {
                        if (day.type === 'padding') {
                            return <div key={`pad-${index}`} className="w-4 h-4" />;
                        }
                        const { cellClass, title } = colorLogic(day);
                        return (
                            <div 
                                key={day.dateString}
                                className={`w-4 h-4 rounded-sm transition-all duration-200 ${day.clickable ? 'cursor-pointer hover:scale-125' : ''} ${cellClass}`}
                                title={title}
                                onClick={() => day.clickable && onDayClick(day.dateString)}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const WellnessGrid = ({ habits, displayYear }) => {
    const [isOpen, setIsOpen] = useState(true);

    const wellnessData = useMemo(() => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const year = displayYear;
        
        const completionsByDate = new Map();
        habits.forEach(habit => {
            habit.completions.forEach(dateString => {
                if (dateString.startsWith(year.toString())) {
                    completionsByDate.set(dateString, (completionsByDate.get(dateString) || 0) + 1);
                }
            });
        });

        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31);
        
        const days = [];
        for (let i = 0; i < startDate.getDay(); i++) { days.push({ type: 'padding' }); }

        const date = new Date(startDate);
        while (date <= endDate) {
            days.push({
                type: 'day', 
                date: new Date(date), 
                dateString: date.toISOString().split('T')[0],
                completions: completionsByDate.get(date.toISOString().split('T')[0]) || 0,
                isToday: date.toDateString() === today.toDateString(),
                month: date.getMonth(), 
                clickable: false,
            });
            date.setDate(date.getDate() + 1);
        }
        
        const monthColumns = [];
        let currentMonth = -1;
        days.forEach((day, dayIndex) => {
            if (day.type === 'day' && day.month !== currentMonth) {
                currentMonth = day.month;
                monthColumns.push({ month: MONTH_LABELS[currentMonth], startColumn: Math.floor(dayIndex / 7) + 1 });
            }
        });

        return { days, monthColumns };
    }, [habits, displayYear]);
    
    const wellnessColorLogic = (day) => {
        const today = new Date();
        today.setHours(0,0,0,0);

        if (day.date > today) {
            return { cellClass: 'bg-gray-800/40', title: day.date.toDateString() };
        }

        const count = day.completions;
        const totalHabits = habits.length;
        let brightnessIndex = 0;
        if (totalHabits > 0) {
            const percentage = count / totalHabits;
            brightnessIndex = Math.min(WELLNESS_COLORS.length - 1, Math.round(percentage * (WELLNESS_COLORS.length - 1)));
        }
        let cellClass = WELLNESS_COLORS[brightnessIndex] || WELLNESS_COLORS[0];
        
        if (day.isToday) {
             cellClass += ` ring-2 ring-offset-2 ring-offset-gray-900 ring-blue-400`;
        }

        return {
            cellClass,
            title: `${day.date.toDateString()} - ${count} of ${totalHabits} habits completed`
        };
    };

    return (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl shadow-lg backdrop-blur-sm mb-6 overflow-hidden">
             <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                <h2 className="text-lg sm:text-xl font-semibold text-white">Overall Wellness - {displayYear}</h2>
                <ChevronIcon isOpen={isOpen} />
             </div>
             <div className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-screen' : 'max-h-0'}`}>
                <div className="p-4 pt-0">
                    <CalendarGrid gridData={wellnessData} colorLogic={wellnessColorLogic} />
                    <p className="text-sm text-gray-400 mt-3">Each square's brightness reflects the number of habits completed on that day.</p>
                </div>
             </div>
        </div>
    );
};

// --- Main Habit Tracker Components ---

const HabitTracker = ({ habit, isOpen, onToggle, onToggleDay, onDeleteHabit, displayYear }) => {
    const gridData = useMemo(() => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const year = displayYear;
        
        const startDate = new Date(year, 0, 1);
        const endDate = new Date(year, 11, 31);

        const days = [];
        for (let i = 0; i < startDate.getDay(); i++) { days.push({ type: 'padding' }); }
        
        const date = new Date(startDate);
        while (date <= endDate) {
            days.push({
                type: 'day', 
                date: new Date(date), 
                dateString: date.toISOString().split('T')[0],
                isCompleted: habit.completions.includes(date.toISOString().split('T')[0]),
                isToday: date.toDateString() === today.toDateString(),
                month: date.getMonth(), 
                clickable: date <= today,
            });
            date.setDate(date.getDate() + 1);
        }

        const monthColumns = [];
        let currentMonth = -1;
        days.forEach((day, dayIndex) => {
            if (day.type === 'day' && day.month !== currentMonth) {
                currentMonth = day.month;
                monthColumns.push({ month: MONTH_LABELS[currentMonth], startColumn: Math.floor(dayIndex / 7) + 1 });
            }
        });
        return { days, monthColumns };
    }, [habit.completions, displayYear]);

    const habitColorLogic = (day) => {
        const color = HABIT_COLORS[habit.colorIndex] || HABIT_COLORS[0];
        
        if (!day.clickable) {
             return { cellClass: 'bg-gray-800/40 cursor-not-allowed', title: day.date.toDateString() };
        }

        let cellClass = day.isCompleted ? color.bg : color.empty;
        
        if (day.isToday) {
            cellClass += ` ring-2 ring-offset-2 ring-offset-gray-900 ring-blue-400`;
        }
        return { cellClass, title: day.date.toDateString() };
    };

    return (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl mb-4 shadow-lg backdrop-blur-sm overflow-hidden">
            <div className="flex justify-between items-center p-4 cursor-pointer" onClick={onToggle}>
                <h2 className="text-lg sm:text-xl font-semibold text-white">{habit.name}</h2>
                <div className="flex items-center gap-4">
                    <TrashIcon onClick={() => onDeleteHabit(habit.id)} />
                    <ChevronIcon isOpen={isOpen}/>
                </div>
            </div>
            <div className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-screen' : 'max-h-0'}`}>
                 <div className="p-4 pt-0">
                    <CalendarGrid gridData={gridData} colorLogic={habitColorLogic} onDayClick={(dateString) => onToggleDay(habit, dateString)} />
                 </div>
            </div>
        </div>
    );
};

const AddHabitModal = ({ isOpen, onClose, onAddHabit, availableColors, existingHabits = [] }) => {
    const [name, setName] = useState('');
    const [selectedColorIndex, setSelectedColorIndex] = useState(-1);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setName('');
            setError('');
            if (availableColors.length > 0) {
                const isSelectedAvailable = availableColors.some(c => c.index === selectedColorIndex);
                if (!isSelectedAvailable || selectedColorIndex === -1) {
                    setSelectedColorIndex(availableColors[0].index);
                }
            }
        }
    }, [isOpen, availableColors]);

    const handleNameChange = (e) => {
        setName(e.target.value);
        if (error) {
            setError('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        const trimmedName = name.trim();

        if (!trimmedName || selectedColorIndex === -1) return;
        
        const isDuplicate = existingHabits.some(
            (habit) => habit.name.trim().toLowerCase() === trimmedName.toLowerCase()
        );

        if (isDuplicate) {
            setError('A habit with this name already exists.');
            return;
        }

        onAddHabit(trimmedName, selectedColorIndex);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md">
                <h2 className="text-xl font-bold text-white mb-4">Add a New Habit</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="habitName" className="block text-sm font-medium text-gray-400 mb-2">Habit Name</label>
                        <input type="text" id="habitName" value={name} onChange={handleNameChange} placeholder="e.g., Yoga - 15 mins" className={`w-full bg-gray-700 text-white border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:outline-none ${error ? 'border-red-500 ring-red-500' : 'focus:ring-blue-500'}`} required />
                        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
                    </div>
                    {availableColors.length > 0 ? (
                        <div className="mb-6">
                            <span className="block text-sm font-medium text-gray-400 mb-2">Color</span>
                            <div className="flex space-x-3">{availableColors.map(color => (<button type="button" key={color.name} onClick={() => setSelectedColorIndex(color.index)} className={`w-8 h-8 rounded-full transition-transform duration-200 ${color.bg} ${selectedColorIndex === color.index ? 'ring-2 ring-offset-2 ring-offset-gray-800 ring-white' : ''}`} aria-label={`Select ${color.name} color`} />))}</div>
                        </div>
                    ) : <p className="text-yellow-400 text-sm mb-4">All colors are in use. Delete a habit to free up a color.</p>}
                    <div className="flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-white hover:bg-gray-700">Cancel</button>
                        <button type="submit" disabled={availableColors.length === 0 || !name.trim()} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed">Add Habit</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-sm">
                <h2 className="text-xl font-bold text-white mb-2">{title}</h2>
                <p className="text-gray-400 mb-6">{children}</p>
                <div className="flex justify-end space-x-3">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-white bg-gray-600 hover:bg-gray-700">Cancel</button>
                    <button onClick={onConfirm} className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700">Confirm</button>
                </div>
            </div>
        </div>
    );
};

/**
 * Main App Component
 */
export default function App() {
    const [userId, setUserId] = useState(null);
    const [habits, setHabits] = useState([]);
    const [openHabits, setOpenHabits] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [deletingHabitId, setDeletingHabitId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [db, setDb] = useState(null);
    
    const currentYear = new Date().getFullYear();
    const [displayYear, setDisplayYear] = useState(currentYear);
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

    useEffect(() => {
        setLogLevel('error');
        try {
            const app = initializeApp(firebaseConfig);
	    const analytics = getAnalytics(app);
            const firestoreDb = getFirestore(app);
            const auth = getAuth(app);
            setDb(firestoreDb);
            onAuthStateChanged(auth, async (currentUser) => {
                if (currentUser) { setUserId(currentUser.uid);
                } else {
                    try {
                         await signInAnonymously(auth); 
                    } catch (error) { console.error("Auth error:", error); }
                }
            });
        } catch (error) { console.error("Firebase init failed:", error); setLoading(false); }
    }, []);

    useEffect(() => {
        if (!db || !userId) return;
        setLoading(true);
        const habitsCollectionPath = `/users/${userId}/habits`;
        const q = query(collection(db, habitsCollectionPath));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const habitsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            habitsData.sort((a, b) => a.createdAt?.seconds - b.createdAt?.seconds);
            setHabits(habitsData);
            if(openHabits.length === 0 && habitsData.length > 0) {
                 setOpenHabits(habitsData.map(h => h.id));
            }
            setLoading(false);
        }, (error) => { console.error("Error fetching habits:", error); setLoading(false); });
        return () => unsubscribe();
    }, [db, userId]);

    const handleAddHabit = async (name, colorIndex) => {
        if (!db || !userId || habits.length >= 5) return;
        const habitsCollectionPath = `/users/${userId}/habits`;
        try {
            await addDoc(collection(db, habitsCollectionPath), { name, colorIndex, completions: [], createdAt: serverTimestamp() });
        } catch (error) { console.error("Error adding habit: ", error); }
    };

    const confirmDeleteHabit = async () => {
        if (!db || !userId || !deletingHabitId) return;
        const habitDocPath = `/users/${userId}/habits/${deletingHabitId}`;
        try {
            await deleteDoc(doc(db, habitDocPath));
            setDeletingHabitId(null);
        } catch (error) { console.error("Error deleting habit: ", error); }
    };

    const handleToggleDay = async (habit, dateString) => {
        if (!db || !userId) return;
        const habitDocPath = `/users/${userId}/habits/${habit.id}`;
        const updatedCompletions = habit.completions.includes(dateString)
            ? habit.completions.filter(d => d !== dateString)
            : [...habit.completions, dateString];
        try { await setDoc(doc(db, habitDocPath), { completions: updatedCompletions }, { merge: true });
        } catch (error) { console.error("Error updating completions: ", error); }
    };
    
    const toggleHabitOpen = (habitId) => {
        setOpenHabits(prev => 
            prev.includes(habitId) 
                ? prev.filter(id => id !== habitId)
                : [...prev, habitId]
        );
    };

    const availableColors = useMemo(() => {
        const usedIndexes = habits.map(h => h.colorIndex);
        return HABIT_COLORS.map((color, index) => ({ ...color, index })).filter(color => !usedIndexes.includes(color.index));
    }, [habits]);

    const renderContent = () => {
        if (loading && !habits.length) return <div className="text-center text-gray-400 mt-10">Loading Habits...</div>;
        if (habits.length === 0) {
            return (
                <div className="text-center mt-10 p-8 bg-gray-800/50 border border-gray-700 rounded-xl">
                    <h2 className="text-2xl font-bold text-white mb-2">Welcome!</h2>
                    <p className="text-gray-400 mb-6">Track up to 5 daily habits. Let's add your first one.</p>
                    <button onClick={() => setIsAddModalOpen(true)} className="flex items-center justify-center mx-auto px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all shadow-lg">
                       <AddIcon /> Add First Habit
                    </button>
                </div>
            );
        }
        return (
            <>
                <WellnessGrid habits={habits} displayYear={displayYear} />
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">My Habits</h2>
                {habits.map(habit => (
                    <HabitTracker 
                        key={habit.id} 
                        habit={habit}
                        displayYear={displayYear}
                        isOpen={openHabits.includes(habit.id)}
                        onToggle={() => toggleHabitOpen(habit.id)}
                        onToggleDay={handleToggleDay} 
                        onDeleteHabit={setDeletingHabitId} 
                    />
                ))}
            </>
        );
    };

    return (
        <div className="bg-gray-900 min-h-screen font-sans text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
             <link rel="stylesheet" href="https://rsms.me/inter/inter.css" />
             <div className="absolute inset-0 z-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 25% 30%, #1e40af 0%, #111827 35%)' }} />
            <div className="relative z-10 container mx-auto p-4 sm:p-6 md:p-8 max-w-5xl">
                <header className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Habit Tracker</h1>
                         <p className="text-gray-400 text-sm">User ID: {userId || 'Connecting...'}</p>
                    </div>
                    {habits.length > 0 && habits.length < 5 && (
                         <button onClick={() => setIsAddModalOpen(true)} className="flex items-center px-4 py-2 rounded-lg bg-gray-700/80 text-white font-medium hover:bg-gray-700 transition-all backdrop-blur-sm border border-gray-600">
                            <AddIcon /> Add Habit
                        </button>
                    )}
                </header>
                
                {habits.length > 0 && <YearNavigator selectedYear={displayYear} years={years} onYearChange={setDisplayYear} />}

                <main>{renderContent()}</main>

                <footer className="text-center text-gray-600 mt-12 text-sm"><p>Your data is saved automatically.</p></footer>
            </div>
            <AddHabitModal 
                isOpen={isAddModalOpen} 
                onClose={() => setIsAddModalOpen(false)} 
                onAddHabit={handleAddHabit} 
                availableColors={availableColors}
                existingHabits={habits}
            />
            <ConfirmationModal isOpen={!!deletingHabitId} onClose={() => setDeletingHabitId(null)} onConfirm={confirmDeleteHabit} title="Delete Habit?">
                Are you sure you want to delete this habit? This action cannot be undone.
            </ConfirmationModal>
        </div>
    );
}

